import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Trophy, Euro } from 'lucide-react';

const WinnersDisplay = ({ market, winners }) => {
  const { t, language } = useLanguage();

  if (market.status !== 'resolved' || !market.resolution) {
    return null;
  }

  const totalPayout = winners?.reduce((sum, w) => sum + (w.payout || 0), 0) || 0;

  return (
    <Card className="border-2 border-green-600/30 bg-green-950/20" data-testid="winners-display">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          {t('marketResolved')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resolution Result */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
          <span className="text-muted-foreground">{t('winners')}:</span>
          <Badge className={market.resolution === 'yes' ? 'bg-green-600 text-lg px-4 py-1' : 'bg-red-600 text-lg px-4 py-1'}>
            {market.resolution.toUpperCase()}
          </Badge>
        </div>

        {/* Total Payout */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
          <span className="text-muted-foreground">{t('totalPayout')}:</span>
          <div className="flex items-center gap-2">
            <Euro className="w-5 h-5 text-yellow-500" />
            <span className="font-mono text-xl font-bold text-green-400">
              +{totalPayout.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Winners List */}
        {winners && winners.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">{t('winnersList')}:</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {winners.map((winner, index) => (
                <div 
                  key={winner.id || index}
                  className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                      ${index === 0 ? 'bg-yellow-500 text-black' : 
                        index === 1 ? 'bg-gray-400 text-black' : 
                        index === 2 ? 'bg-amber-700 text-white' : 'bg-secondary'}`}
                    >
                      {index + 1}
                    </span>
                    <span className="font-medium">{winner.username || 'Anonym'}</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono text-green-400">+{winner.payout?.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}</span>
                    <p className="text-xs text-muted-foreground">
                      {t('betAmount')}: {winner.amount?.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!winners || winners.length === 0) && (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t('noWinners')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default WinnersDisplay;
